import { useCallback, useEffect, useRef, useState } from 'react';

import { PrimaryButton } from 'approot/shared/styles/buttons';
import IconClose from 'approot/shared/icons/icon-close';
import { Dropdown } from 'approot/shared/dropdown/dropdown';
import { PStyled } from 'approot/shared/styles/typography.style';
import useUserData from 'approot/shared/user/user-data';
import {
  addEventListenerSizeChange,
  removeEventListenerSizeChange,
} from 'lib/events/size';
import { Regions, REGION_OPTIONS } from './region.constants';
import {
  setRegionBannerClose,
  setRegionBannerHeight,
  useRegionData,
} from './region.apollo';
import * as Styled from './region.style';

const RegionsBanner = () => {
  const { isSignedIn } = useUserData();
  const { regionBanner } = useRegionData();

  const ref = useRef<HTMLDivElement | null>(null);
  const [currentRegion, setCurrentRegion] = useState<Regions>(
    regionBanner.region
  );

  const onResize = useCallback(() => {
    setRegionBannerHeight(ref.current);
  }, []);

  useEffect(() => {
    if (!regionBanner.called) {
      if (ref.current) {
        // Suspense adds display: none; which messes with the height
        // of the banner. Removing it here as the element is ready
        ref.current.style.display = 'block';
      }
      setRegionBannerHeight(ref.current);
    }
    addEventListenerSizeChange(onResize);
    return () => {
      removeEventListenerSizeChange(onResize);
    };
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isSignedIn && regionBanner.isVisible) {
      setRegionBannerClose();
    }
    //eslint-disable-next-line
  }, [isSignedIn]);

  return regionBanner.isVisible && !isSignedIn ? (
    <Styled.RegionsBanner ref={ref} data-selector="change-region-banner">
      <Styled.RegionsBannerContent>
        <PStyled>
          It looks like you’re in a different country. Change country to see
          content specific to your location.
        </PStyled>
        <Styled.RegionsBannerControls>
          <Dropdown
            aria-label="Change region"
            selectedValue={currentRegion}
            options={REGION_OPTIONS}
            onSelect={option => setCurrentRegion((option.value as Regions)!)}
          />
          <PrimaryButton
            type="button"
            aria-label={`Change to ${
              REGION_OPTIONS.find(r => r.value === currentRegion)?.label
            } website`}
            onClick={() => {
              window.location.replace(
                `${window.location.origin}/${currentRegion}`
              );
            }}
            data-selector="change-region-btn"
          >
            Continue
          </PrimaryButton>
        </Styled.RegionsBannerControls>
        <Styled.RegionsBannerClose
          type="button"
          aria-label="Close region selection banner"
          onClick={setRegionBannerClose}
        >
          <IconClose width={16} height={16} />
        </Styled.RegionsBannerClose>
      </Styled.RegionsBannerContent>
    </Styled.RegionsBanner>
  ) : null;
};

export default RegionsBanner;
